/**
 * Player-session persistence so a player keeps their seat across a tab reload (#52).
 *
 * The player's WS ticket lives only in React state (`PlayerPage`), so a reload used to
 * drop it and bounce the player back to the "Join the game" form. A *registered*
 * player recovered because their name (their Cognito username) is persisted and an
 * effect auto-re-joins under it — but a **guest** had no persisted name and no
 * auto-join, so every reload sent them to the form, regardless of whether they joined
 * by link, QR, or by typing the code. This is the player's equivalent of
 * {@link ./host-session.ts} — the host had the same disease and the same cure.
 *
 * We persist the durable *handle* to the game the player is in — its `gameId`,
 * `joinCode`, and the display `name` used to join — NOT the ticket. On reload
 * `PlayerPage` re-joins via the normal `joinAsPlayer` path: identity + seat ownership
 * come from the persisted signed guest token (FR-9.3), and `GameActor.seatPlayer` is
 * idempotent by `playerId`, so the returning player keeps their seat and score
 * (FR-2.6). The `name` is stored because a guest — unlike a registered player — has no
 * other source for the display name the re-join call needs.
 *
 * Scoped to prod (the deployed control plane); the dev server has a single
 * auto-hosted game with no join code, so a dev reload re-joins the one game with no
 * handle needed.
 */

const PLAYER_GAME_ID_KEY = 'jeopardy.player.gameId';
const PLAYER_JOIN_CODE_KEY = 'jeopardy.player.joinCode';
const PLAYER_NAME_KEY = 'jeopardy.player.name';

/** The persisted handle to a game this browser is playing in. */
export interface PlayerSession {
  gameId: string;
  joinCode: string;
  name: string;
}

/** Read the persisted player session, or null if none (any field missing). */
export function loadPlayerSession(): PlayerSession | null {
  // SSR-safe: the multi-route prerender (#241) renders PlayerPage under Node, where there
  // is no `localStorage` — return null there rather than throwing (mirrors loadIdentity).
  if (typeof localStorage === 'undefined') return null;
  const gameId = localStorage.getItem(PLAYER_GAME_ID_KEY);
  const joinCode = localStorage.getItem(PLAYER_JOIN_CODE_KEY);
  const name = localStorage.getItem(PLAYER_NAME_KEY);
  if (!gameId || !joinCode || !name) return null;
  return { gameId, joinCode, name };
}

/** Persist the player session so a reload can auto-rejoin the same game + seat. */
export function savePlayerSession(session: PlayerSession): void {
  localStorage.setItem(PLAYER_GAME_ID_KEY, session.gameId);
  localStorage.setItem(PLAYER_JOIN_CODE_KEY, session.joinCode);
  localStorage.setItem(PLAYER_NAME_KEY, session.name);
}

/** Drop the persisted player session (game over / game gone / joining a new game). */
export function clearPlayerSession(): void {
  localStorage.removeItem(PLAYER_GAME_ID_KEY);
  localStorage.removeItem(PLAYER_JOIN_CODE_KEY);
  localStorage.removeItem(PLAYER_NAME_KEY);
}
